import { sanitizeLanguageCode } from '@procureai/schemas';
import ky from 'ky';
import { v4 as uuidv4 } from 'uuid';

import { appConfig } from '~/common/config';
import { i18n } from '~/common/i18n';
import { getActiveScope } from '~/services/activeScope';
import { userManager } from '~/services/oidc';

const setHeaders = async (headers: Headers) => {
  const user = await userManager.getUser();
  headers.set('Authorization', `Bearer ${user?.access_token}`);
  headers.set('X-Request-Id', uuidv4());
  headers.set('Accept-Language', sanitizeLanguageCode(i18n.language)); // at execution time gets most recently selected language
  headers.set('X-Scope-Id', getActiveScope());
};

const getOrigin = (url: string | undefined) => {
  try {
    return url ? new URL(url, window.location.origin).origin : undefined;
  } catch {
    return undefined;
  }
};

const datagraphOrigin = getOrigin(appConfig.datagraph.url);

// Singular (the datagraph) authenticates via `Authorization: Bearer` and sets no cookies, so its
// requests must NOT be credentialed — otherwise the browser requires an
// `Access-Control-Allow-Credentials: true` response header, which the Bearer-only gateway
// (correctly) does not send, and drops the response. Strip credentials for datagraph requests
// regardless of caller, while keeping them for the cookie-bearing endpoints (mastra, config).
const uncredentialDatagraphRequest = (request: Request) =>
  datagraphOrigin && request.credentials !== 'omit' && getOrigin(request.url) === datagraphOrigin
    ? new Request(request, { credentials: 'omit' })
    : undefined;

export const fetchClient = ky.create({
  credentials: 'include',
  // do not throw http errors as better-auth cannot get the error message/code from the response
  throwHttpErrors: false,
  timeout: appConfig.http.timeout,
  hooks: {
    beforeRequest: [
      async ({ request }) => {
        await setHeaders(request.headers);
      },
      ({ request }) => uncredentialDatagraphRequest(request),
    ],
  },
});
